import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismNumberInput } from '@mylighthouse/prism-ember';
import type { RecommendationStrategy } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  NUMBER_INPUT_MAX_VALUE,
  PMS_SETTINGS_OFFSET_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import {
  flattenRoomOffsets,
  offsetToDisplayValue,
  offsetToStoredValue,
} from 'lumen/utils/pricing-assistant/pms-integration/seasonal-room-hierarchy';
import {
  resolveOffsetValue,
  updateHierarchy,
} from 'lumen/utils/pricing-assistant/room-hierarchy-settings';

import styles from './seasonal-room-offset-table.module.css';

interface RoomOffsetRow {
  offsetValue: number | null;
  roomId: string;
  roomName: string;
}

interface SettingsPmsIntegrationSeasonalRoomOffsetTableSignature {
  Args: {
    // Read-only shows the offsets as disabled inputs (the expanded card view).
    isReadOnly: boolean;
    strategy: RecommendationStrategy;
  };
}

export default class SettingsPmsIntegrationSeasonalRoomOffsetTable extends Component<SettingsPmsIntegrationSeasonalRoomOffsetTableSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  // The mode currently is hotel-wide, so one unit applies to all rows — read it off the
  // first offset.
  // Note: deriving offset type from the seasonal hierarchy strategy data.
  // the idea is that in the future we'll allow users to pick absolute/relative offset type per seasonal strategy
  get offsetUnit(): string {
    const { settings } = this.args.strategy;

    if (settings.settings_type !== 'room_offset_hierarchy') {
      return '';
    }

    const [firstOffset] = flattenRoomOffsets(settings.hierarchy);

    return firstOffset?.offset.type === PMS_SETTINGS_OFFSET_TYPES.RELATIVE
      ? '%'
      : this.hotelAndMarketService.hotel.currencySymbol;
  }

  /*
   * One row per (non-root) room, showing its display value (e.g. 10 for a 10%
   * relative offset). Sorted alphabetically by room name to match the PMS page,
   * which sorts the hierarchy (the BE doesn't guarantee its order).
   */
  @cached
  get rows(): RoomOffsetRow[] {
    const { settings } = this.args.strategy;

    if (settings.settings_type !== 'room_offset_hierarchy') {
      return [];
    }

    const roomNameById =
      this.hotelAndMarketService.hotel?.pricingIntegration
        ?.pms_id_to_pms_room_name;

    return flattenRoomOffsets(settings.hierarchy)
      .map(({ offset, roomCode }) => ({
        offsetValue: offsetToDisplayValue(offset.value, offset.type),
        roomId: roomCode,
        roomName: roomNameById?.[roomCode] ?? roomCode,
      }))
      .sort((a, b) =>
        a.roomName < b.roomName ? -1 : a.roomName > b.roomName ? 1 : 0,
      );
  }

  /*
   * Writes a display value back into the hierarchy as a stored fraction
   * (relative) or passthrough (absolute), reassigning settings so EmberData
   * tracks the change. The editor's save persists it; cancel/close rolls it back.
   */
  writeOffsetValue(roomId: string, value: number | null): void {
    const { strategy } = this.args;
    const { settings } = strategy;

    if (settings.settings_type !== 'room_offset_hierarchy') {
      return;
    }

    strategy.settings = {
      ...settings,
      hierarchy: updateHierarchy(settings.hierarchy, (roomCode, node) => {
        if (roomCode !== roomId) {
          return;
        }

        return {
          action: 'update',
          roomCode,
          node: {
            ...node,
            offset: {
              ...node.offset,
              value: offsetToStoredValue(
                resolveOffsetValue(value),
                node.offset.type,
              ),
            },
          },
        };
      }),
    };
  }

  /*
   * Commits an emptied field on blur as 0 (writeOffsetValue coerces null);
   * non-empty edits are already written live by onOffsetInput.
   */
  @action
  commitOffset(roomId: string, event: Event): void {
    const target = event.target as HTMLInputElement | null;

    if (target?.value === '') {
      this.writeOffsetValue(roomId, null);
    }
  }

  @action
  onOffsetInput(roomId: string, value: number | null): void {
    // Skip an empty field while typing: the write coerces null to 0, which snaps
    // the input back to 0 mid-edit. Blur commits the 0 instead (commitOffset).
    if (value === null) {
      return;
    }

    this.writeOffsetValue(roomId, value);
  }

  <template>
    <div class="flex flex-col gap-200" data-test-room-offsets>
      {{#each this.rows key="roomId" as |row|}}
        <div
          class="flex flex-row items-center justify-between gap-200"
          data-test-room-offset={{row.roomId}}
        >
          <p class="text-200--regular min-w-0 flex-1 truncate">
            {{row.roomName}}
          </p>

          <div class={{styles.input}}>
            <PrismNumberInput
              @disabled={{@isReadOnly}}
              @inlineLabel={{this.offsetUnit}}
              @isTouched={{true}}
              @max={{NUMBER_INPUT_MAX_VALUE}}
              @onFocusOut={{fn this.commitOffset row.roomId}}
              @onValueChange={{fn this.onOffsetInput row.roomId}}
              @placeholder="0"
              @removePadding={{true}}
              @value={{row.offsetValue}}
            />
          </div>
        </div>
      {{/each}}
    </div>
  </template>
}
